import type { EducationLevel } from './stats.js';
import type { LocationId } from './location.js';

export type ActionId =
  | 'dormir'
  | 'comer-em-casa'
  | 'estudar-em-casa'
  | 'trabalhar'
  | 'candidatar-se'
  | 'estudar'
  | 'comprar-comida'
  | 'comprar-fato'
  | 'beber'
  | 'comer-no-bar'
  | 'socializar';

export type ActionEffects = {
  readonly money?: number;
  readonly energy?: number;
  readonly happiness?: number;
  readonly hunger?: number;
  readonly education?: number;
  readonly foodAtHome?: number;
};

export type ActionRequirement = {
  readonly minEducation?: EducationLevel;
  readonly requiresJob?: boolean;
  readonly minFoodAtHome?: number;
  readonly requiresNoSuit?: boolean;
};

export type Action = {
  readonly id: ActionId;
  readonly locationId: LocationId;
  readonly duration: number;
  readonly effects: ActionEffects;
  readonly requirements?: ActionRequirement;
};

export const ACTIONS: Readonly<Record<ActionId, Action>> = Object.freeze({
  dormir: {
    id: 'dormir',
    locationId: 'casa',
    duration: 2,
    effects: { energy: 15, hunger: -6, happiness: 1 },
  },
  'comer-em-casa': {
    id: 'comer-em-casa',
    locationId: 'casa',
    duration: 1,
    effects: { hunger: 35, happiness: 2, foodAtHome: -1 },
    requirements: { minFoodAtHome: 1 },
  },
  'estudar-em-casa': {
    id: 'estudar-em-casa',
    locationId: 'casa',
    duration: 1,
    effects: { education: 25, energy: -3, hunger: -2, happiness: -1, money: -1000 },
  },
  trabalhar: {
    id: 'trabalhar',
    locationId: 'trabalho',
    duration: 1,
    // money: dinâmico via job actual; engine soma job.hourlyWage * duration
    effects: { energy: -6, hunger: -4, happiness: -2 },
    requirements: { requiresJob: true },
  },
  'candidatar-se': {
    id: 'candidatar-se',
    locationId: 'trabalho',
    duration: 1,
    effects: { happiness: -1, hunger: -1, energy: -1 },
  },
  estudar: {
    id: 'estudar',
    locationId: 'escola',
    duration: 1,
    effects: { education: 50, energy: -4, hunger: -3, happiness: -1, money: -2000 },
  },
  'comprar-comida': {
    id: 'comprar-comida',
    locationId: 'loja',
    duration: 1,
    effects: { money: -4000, foodAtHome: 5, happiness: -1, hunger: -1, energy: -1 },
  },
  'comprar-fato': {
    id: 'comprar-fato',
    locationId: 'loja',
    duration: 1,
    effects: { money: -100_000, happiness: -1, hunger: -1, energy: -1 },
    requirements: { requiresNoSuit: true },
  },
  beber: {
    id: 'beber',
    locationId: 'bar',
    duration: 1,
    effects: { happiness: 18, hunger: -3, money: -2000, energy: -5 },
  },
  'comer-no-bar': {
    id: 'comer-no-bar',
    locationId: 'bar',
    duration: 1,
    effects: { hunger: 35, money: -2500, happiness: 2 },
  },
  socializar: {
    id: 'socializar',
    locationId: 'bar',
    duration: 1,
    effects: { happiness: 15, energy: -3, hunger: -2, money: -1500 },
  },
});

export function actionsAtLocation(locationId: LocationId): readonly Action[] {
  return Object.values(ACTIONS).filter((a) => a.locationId === locationId);
}
